import React, { useCallback, useEffect, useState } from "react";
import { FormHeader } from "@components/Header";
import { useNavigate } from "react-router-dom";
import { FormBody, FormContentContainer } from "@components/Container";
import {
  BottomActionButton,
  SecondaryActionButton,
} from "@components/BottomActionArea";
import { Box, CircularProgress, styled, Typography } from "@mui/material";
import { CancelOutlined } from "@mui/icons-material";
import {
  useCreateBankTransferChallenge,
  useVerifyBankTransferStatus,
} from "@hooks/index";
import { BankTransactionStatus } from "@interfaces/Esign";
import { useSnackbar } from "notistack";
import { tracker } from "@utils/openreplay";

const StyledBottomActionButton = styled(BottomActionButton)({
  gap: 6,
});

const StyledCancelIcon = styled(CancelOutlined)(({ theme }) => ({
  fontSize: "46px",
  color: theme.palette.error.main,
}));

export const CanceledBankTransferScreen: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { data: challengeStatusData, error: errorGettingTransactionStatus } =
    useVerifyBankTransferStatus();

  const { mutateAsync: createChallengeRequest } =
    useCreateBankTransferChallenge({
      onError: (error) => {
        setIsLoading(false);
        tracker?.event("BankTransferChallengeRequestedError", {
          source: "CanceledBankTransferScreen",
          error: error,
          errorMessage: error.message,
        });
        enqueueSnackbar("Error al generar la transferencia bancaria", {
          variant: "error",
        });
      },
    });

  const handleRetry = useCallback(async () => {
    setIsLoading(true);
    await createChallengeRequest();
  }, [createChallengeRequest]);

  useEffect(() => {
    tracker?.event("BankTransferChallengeCanceled", {
      source: "CanceledBankTransferScreen",
    });

    if (
      challengeStatusData?.bankTransferStatus === BankTransactionStatus.APPROVED
    ) {
      navigate(
        "/app/registro/firma-electronica-avanzada/transferencia-bancaria/exito"
      );
    } else if (
      challengeStatusData?.bankTransferStatus === BankTransactionStatus.PENDING
    ) {
      navigate("/app/solicitudes");
    }
  }, [challengeStatusData?.bankTransferStatus, navigate]);

  useEffect(() => {
    if (errorGettingTransactionStatus) {
      tracker?.event("BankTransferChallengeStatusError", {
        source: "CanceledBankTransferScreen",
        error: errorGettingTransactionStatus,
        errorMessage: errorGettingTransactionStatus?.message,
      });
    }
  }, [errorGettingTransactionStatus]);

  return (
    <>
      <FormHeader useMekiDocIcon />
      <FormContentContainer>
        <FormBody justifyContent="center">
          <Box
            marginBottom="48px"
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="16px"
          >
            <StyledCancelIcon />
            <Typography variant="h6" fontWeight="semibold">
              Transferencia cancelada
            </Typography>
          </Box>
          <StyledBottomActionButton onClick={handleRetry} disabled={isLoading}>
            Intentar nuevamente
            {isLoading ? <CircularProgress size={20} color="inherit" /> : null}
          </StyledBottomActionButton>

          <SecondaryActionButton
            onClick={() => navigate("/app/solicitudes")}
            disabled={isLoading}
          >
            Volver a inicio
          </SecondaryActionButton>
        </FormBody>
      </FormContentContainer>
    </>
  );
};
